import React, { useState, useEffect, useCallback, useRef } from "react";

export const TESTIMONIALS = [
  {
    quote:
      "Research Mode outlines the specific elements of my claim and flags areas I hadn't considered researching further. Debate Mode's Critical Analyst surfaces counterarguments I hadn't thought of, which helps me be better prepared.",
    name: "Caitlin Newswanger",
    title: "Post-Conviction Attorney",
    practiceAreas: [
      { text: "Post-Conviction Law", class: "criminal" },
      { text: "Criminal Litigation", class: "litigation" }
    ],
    image: "/caitlin-newswanger.jpg",
    initials: null
  },
  {
    quote:
      "LawFusion is an excellent tool. It combines multiple AI perspectives, clearly shows where the models agree and disagree, and creates useful executive summaries. Research Mode and Debate Mode serve different purposes and are both useful for my practice. I highly recommend it.",
    name: "Brian Heinze, Esq.",
    title: "Atlanta, Georgia Attorney",
    practiceAreas: [
      { text: "Transactional Law", class: "transactional" },
      { text: "Contracts", class: "contracts" }
    ],
    image: "/brian-heinze.jpg",
    initials: null
  },
  {
    quote:
      "Debate Mode\u2019s Advocate, Critic, and Strategist produced materially different perspectives that pressure-tested the claims, defenses, and evidence. LawFusion is a valuable way to organize issues, identify factual gaps, and sharpen strategy before taking a litigation position.",
    name: "Evan Miller, Esq.",
    title: "Florida Attorney",
    practiceAreas: [
      { text: "Business / Corporate Law", class: "business" },
      { text: "Contracts", class: "contracts" }
    ],
    image: "/Headshot- Evan Miller.jpeg",
    initials: null
  },
  {
    quote:
      "LawFusion transforms complex, unstructured fact patterns into clear, litigation-focused analysis. It identifies the most legally significant issues, exposes risks and missing facts, and organizes the analysis into practical strategic recommendations. The result is a powerful legal reasoning tool that can sharpen case assessment, strengthen litigation strategy, and meaningfully improve attorney efficiency and decision-making.",
    name: "Ella Twente",
    title: "Litigation & Corporate Attorney",
    practiceAreas: [
      { text: "Civil Litigation", class: "litigation" },
      { text: "Business / Corporate Law", class: "business" }
    ],
    image: null,
    initials: "ET"
  },
  {
    quote:
      "LawFusion’s Debate Mode was genuinely impressive. Both the Standard and Adversarial panels developed the arguments from both sides, identified many of the same considerations the court relied on, and provided a powerful framework for pressure-testing legal strategy before filing or oral argument.",
    name: "Nyssa Olsen",
    title: "Family Law & Estates Paralegal",
    practiceAreas: [
      { text: "Family Law", class: "family" },
      { text: "Estates & Wills", class: "estates" },
      { text: "Trial Prep", class: "prep" }
    ],
    image: "/Nyssa_Olsen.png",
    initials: null
  },
  {
    quote:
      "LawFusion is one of the most practical AI legal tools I’ve tested. It goes beyond generic AI responses by identifying key issues, exposing weaknesses, and presenting strategic counterarguments that are genuinely useful in litigation. As a litigation paralegal working across multiple jurisdictions, I recommend it to legal professionals looking to strengthen their research and case strategy.",
    name: "Ashley Allen, CP",
    title: "Certified Paralegal",
    practiceAreas: [
      { text: "Litigation", class: "litigation" },
      { text: "Case Strategy", class: "strategy" }
    ],
    image: "/_TestimonialAshley.jpeg",
    initials: null
  }
];

// Left arrow SVG
function ArrowLeft() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <polyline points="15 18 9 12 15 6" />
    </svg>
  );
}

// Right arrow SVG
function ArrowRight() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <polyline points="9 18 15 12 9 6" />
    </svg>
  );
}

export default function NewLandingTestimonials() {
  const [active, setActive] = useState(0);
  const [direction, setDirection] = useState("next"); // "next" | "prev"
  const [animating, setAnimating] = useState(false);
  const timerRef = useRef(null);
  const total = TESTIMONIALS.length;

  const startTimer = useCallback(() => {
    clearInterval(timerRef.current);
    timerRef.current = setInterval(() => {
      navigate("next");
    }, 6000);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    TESTIMONIALS.forEach((t) => {
      if (t.image) {
        const img = new Image();
        img.src = t.image;
      }
    });
  }, []);

  useEffect(() => {
    startTimer();
    return () => clearInterval(timerRef.current);
  }, [startTimer]);

  const navigate = useCallback(
    (dir, targetIdx) => {
      if (animating) return;
      setAnimating(true);
      setDirection(dir);
      setTimeout(() => {
        setActive((prev) => {
          if (targetIdx !== undefined) return targetIdx;
          return dir === "next" ? (prev + 1) % total : (prev - 1 + total) % total;
        });
        setAnimating(false);
      }, 280);
    },
    [animating, total]
  );

  const handlePrev = () => {
    navigate("prev");
    startTimer();
  };

  const handleNext = () => {
    navigate("next");
    startTimer();
  };

  const handleDot = (idx) => {
    if (idx === active) return;
    navigate(idx > active ? "next" : "prev", idx);
    startTimer();
  };

  const t = TESTIMONIALS[active];

  const cardClass = [
    "testimonial-card",
    animating ? (direction === "next" ? "tcard-exit-left" : "tcard-exit-right") : "tcard-enter"
  ].join(" ");

  return (
    <section className="testimonials-section" id="testimonials">
      <div className="testimonials-inner">
        <p className="section-eyebrow">Attorney Testimonials</p>
        <h2 className="section-headline">What Attorneys Are Saying About LawFusion</h2>

        <div className="carousel-wrap">
          {/* Left arrow */}
          <button
            className="carousel-arrow arrow-prev"
            onClick={handlePrev}
            aria-label="Previous testimonial"
          >
            <ArrowLeft />
          </button>

          {/* Card */}
          <div className={cardClass}>
            <div className="slides-container">
              <div className="slide active" style={{ display: "flex", flexDirection: "column", height: "100%" }}>
                <div className="practice-labels">
                  {t.practiceAreas.map((area, aIdx) => (
                    <span key={aIdx} className={`practice-label ${area.class}`}>
                      {area.text}
                    </span>
                  ))}
                </div>
                {/* Star rating */}
                <div className="stars-row" aria-label="5 out of 5 stars">
                  {[1,2,3,4,5].map((s) => (
                    <svg key={s} className="star-icon" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                      <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                    </svg>
                  ))}
                </div>
                <span className="quote-mark" aria-hidden="true">"</span>
                <p className="quote-text">{t.quote}</p>
                <div className="attorney-row">
                  {t.initials ? (
                    <div className="avatar-initials" aria-hidden="true">{t.initials}</div>
                  ) : (
                    <div className="avatar-headshot">
                      <img src={t.image} alt={t.name} />
                    </div>
                  )}
                  <div className="attorney-info">
                    <div className="attorney-name">{t.name}</div>
                    <div className="attorney-title">{t.title}</div>
                    <span className="verified-badge">
                      <svg viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                        <path fillRule="evenodd" d="M6.267 3.455a3.066 3.066 0 001.745-.723 3.066 3.066 0 013.976 0 3.066 3.066 0 001.745.723 3.066 3.066 0 012.812 2.812c.051.643.304 1.254.723 1.745a3.066 3.066 0 010 3.976 3.066 3.066 0 00-.723 1.745 3.066 3.066 0 01-2.812 2.812 3.066 3.066 0 00-1.745.723 3.066 3.066 0 01-3.976 0 3.066 3.066 0 00-1.745-.723 3.066 3.066 0 01-2.812-2.812 3.066 3.066 0 00-.723-1.745 3.066 3.066 0 010-3.976 3.066 3.066 0 00.723-1.745 3.066 3.066 0 012.812-2.812zm7.44 5.252a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                      </svg>
                      Verified Legal Professional
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Right arrow */}
          <button
            className="carousel-arrow arrow-next"
            onClick={handleNext}
            aria-label="Next testimonial"
          >
            <ArrowRight />
          </button>

          {/* Navigation dots */}
          <div className="testimonial-dots">
            {TESTIMONIALS.map((_, idx) => (
              <button
                key={idx}
                className={`testimonial-dot${idx === active ? " active" : ""}`}
                onClick={() => handleDot(idx)}
                aria-label={`Go to testimonial ${idx + 1}`}
              />
            ))}
          </div>
        </div>

        {/* Commented out until the dedicated Attorney Reviews page is live */}
        {/*
        <div className="see-all-wrap">
          <a href="/testimonials" className="see-all-link" onClick={(e) => e.preventDefault()}>
            See All Attorney Testimonials
          </a>
        </div>
        */}

        <p className="section-disclosure">
          Some testimonials are from compensated testers who received complimentary access.
        </p>
      </div>
    </section>
  );
}
